import { Component, Event, EventEmitter, getAssetPath, h, Host, State } from '@stencil/core';

import wcShared from '@web-components/shared';

import { CallmebackReservations, CallmebackSlot, CallmebackSlots } from './callmeback.type';
import { Cancel } from './cancel/Cancel';
import callmebackService from './services/callmeback.service';
import { InitStore } from '../../../../utils/Stores/init-store';
import { Store } from '../../../../utils/Stores/store';

/** Minimum time from now for a slot to be available */
const SLOT_MIN_DIFF_MIN = 10;
const DAY_DURATION_IN_MS = 1000 * 60 * 60 * 24;
const MINUTE_DURATION_IN_MS = 60 * 1000;

@Component({
  tag: 'contact-callmeback',
  styleUrl: 'contact-callmeback.scss',
  shadow: false,
  scoped: true,
})
export class ContactCallmeback {
  @State() action = 'edit';
  @State() isCancelled = false;
  @State() days: CallmebackSlots[] = [];
  @State() phone: string = Boolean(InitStore?.get('init').user)
    ? InitStore?.get('init').user['reminder_phone']
    : null;
  @State() token: string = null;
  @State() error: string = null;

  @Event() closeEvent: EventEmitter;
  @Event() sectionChangeEvent: EventEmitter<IWCContextClose[]>;

  /**
   * This component renders three states: the CMB creation form, the same form but in edit mode, and a cancellation screen
   * Edit/cancel mode are managed exclusively via the chatbot in fullscreen mode on infomaniak.com/fr/callmeback (via a link in an email that redirects to the Infomaniak site)
   * The window var CHATBOT_CALLMEBACK is used to determine if we are on infomaniak.com/fr/callmeback (only in edit/cancel mode via email link)
   * This component/logic needs to be reworked to make everything clearer
   */
  componentWillLoad() {
    const searchParams = new URL(window.location.href).searchParams;

    this.action = searchParams.get('action') === 'cancel' ? 'cancel' : 'edit';

    // Check if an operation is in progress (we need to allow user to access CMB for commercial operation)
    const operationParam = searchParams.get('operation');
    const OPERATION_NAME = 'migration'; // Rename this if the operation name changes in the future
    const isMigrationEnabled = operationParam === OPERATION_NAME && false; // Set this to true when we want to enable migration again

    this.token = searchParams.get('token');
    if (Boolean(this.token)) {
      callmebackService
        .getReservationByToken(this.token)
        .then((data) => (this.phone = data.phone))
        .catch(() => this.goToSection('landing'));
      // If there is no token and we are in fullscreen mode (CHATBOT_FULL = true) on infomaniak.com/fr/callmeback, we redirect to the chatbot homepage (EXCEPT if a marketing operation is in progress)
    } else if (window['CHATBOT_CALLMEBACK'] && !isMigrationEnabled) {
      this.goToSection('landing');
    }

    // We only have two actions: edit and cancel. We use 'edit' also in creation mode
    if (this.action === 'edit') {
      this.fetchReservations();
    }
  }

  private parseReservation(reservation: CallmebackReservations) {
    const now = new Date();
    const date = new Date(reservation.day);

    const res = {
      isWeekend: date.getDay() === 6 || date.getDay() === 0,
      labelIsDate: true,
      label: null,
      date: reservation.day,
      disabled: !Boolean(reservation.slots.find((current) => current['is_available'])),

      /**
       * Map slots between `morning` and `afternoon`,
       * force availability to be false if time diff is insufficent
       */
      ...reservation.slots.reduce(
        (reservation, slot) => {
          /** Split slot time components */
          const [hours, minutes] = (slot.start.split(':') as [`${number}`, `${number}`]).map(
            Number,
          );

          /** Offset the reservation's date with the slot's hours/minutes */
          const slotDate = new Date(date.getTime());
          slotDate.setHours(hours, minutes, 0, 0);

          /** Get a slot's time diff from now in minutes */
          const diffMin = (Number(slotDate) - Number(now)) / MINUTE_DURATION_IN_MS;

          /**
           * Force a slot to be unavailable if it's in the past or
           * it is less than XX min from now
           */
          const isAvailable = slot.is_available && diffMin > SLOT_MIN_DIFF_MIN;

          /** Add the slot to the reservation */
          const type = hours <= 12 ? ('morning' as const) : ('afternoon' as const);
          reservation[type].push({ ...slot, is_available: isAvailable });

          return reservation;
        },
        { morning: [], afternoon: [] } as Record<'morning' | 'afternoon', CallmebackSlot[]>,
      ),
    };

    /** Get a slot's time diff from now in days */
    const today = new Date(now.getTime());
    today.setHours(0, 0, 0, 0);
    const diffDay = (date.getTime() - today.getTime()) / DAY_DURATION_IN_MS;

    if (diffDay < 1) {
      res.label = wcShared?.translate("Aujourd'hui");
      res.labelIsDate = false;
    } else if (diffDay < 2) {
      res.label = wcShared?.translate('Demain');
      res.labelIsDate = false;
    } else {
      const lang = wcShared?.store?.utils?.getLang();
      res.label = date.toLocaleString(lang + '-' + lang.toUpperCase(), {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      });
    }
    return res;
  }

  private fetchReservations() {
    callmebackService
      .getReservations(this.token)
      .then((data) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        this.days = data.map(this.parseReservation);
      })
      .catch((e) => {
        if (!Boolean(InitStore?.get('init')?.user)) this.goToSection('landing');
        this.token = null;
        this.action = 'edit';
        this.error = e.message;
      });
  }

  private goToSection(section: IWCContextClose) {
    if (section === 'landing') window['CHATBOT_CALLMEBACK'] = false;
    this.sectionChangeEvent.emit([section]);
  }

  private onSlotIsCanceled = () =>
    callmebackService.deleteReservation(this.token).then(() => (this.isCancelled = true));

  private onFetchingReservation = (error: string) => {
    this.error = error;
    this.fetchReservations();
  };

  private onChatBotClose = () => {
    this.closeEvent.emit();
    this.goToSection('landing');
  };

  render() {
    return (
      <Host>
        <div class={{ title: true, hide: this.action === 'cancel' && !Store?.get('isMobile') }}>
          <img
            alt="headphones svg"
            src={getAssetPath('./../assets/rounded-icons/icon-headset-white-bg.svg')}
          />
          <div class={{ white: Store?.get('is_full') }}>
            {wcShared?.translate('Support Infomaniak')}
          </div>
        </div>
        {this.action === 'cancel' && (
          <Cancel
            showConfirm={!this.isCancelled}
            cancel={this.onSlotIsCanceled}
          ></Cancel>
        )}
        {this.action === 'edit' && (
          <contact-callmeback-form
            days={this.days}
            close={this.onChatBotClose}
            phoneNumber={this.phone}
            token={this.token}
            error={this.error}
            fetchReservations={this.onFetchingReservation}
          ></contact-callmeback-form>
        )}
      </Host>
    );
  }
}
